import React from 'react';

import { correctingTrailingNumbers, bemCssModules } from '@cryptoboyio/mercuryo-lab/utils';
import { useTranslation } from 'react-i18next';

import { useStore } from '@modules';
import { CryptoCurrencyEnum, CurrencyEnum } from '@modules/global/types';
import { OperationsEnum, PeriodsEnum, TLimits } from '@modules/wallet/types';

import style from './Limits.scss';

const cn = bemCssModules(style, 'Limits');
const getPeriodPercent = (value: string, limit: string) => (1 - +value / +limit) * 100;

export type TLimitBlock = {
  operation: OperationsEnum;
  limits: TLimits[OperationsEnum];
  currency: CryptoCurrencyEnum;
  fiatCurrency: CurrencyEnum;
  min?: string;
  max?: string;
};

export const LimitBlock: CustomFC<TLimitBlock> = props => {
  const { operation, limits, min, max, currency, fiatCurrency } = props;

  const { t } = useTranslation();

  const [{ global }] = useStore();

  const { currencies } = global;

  const displayDigits = currencies.config.display_options[currency]?.display_digits;
  const totalDigits = currencies.config.display_options[currency]?.total_digits;

  return (
    <div className={cn('blocks-group')}>
      <div className={cn('blocks-title')}>{t(`limits.${operation}`)}</div>
      {Object.entries(limits).map(([period, limit]) => {
        if (!Object.values(limit).length) return null;

        const percent = getPeriodPercent(limit.value, limit.init_value);
        return (
          <div key={period} className={cn('block')}>
            <div className={cn('block-title')}>
              {t(`limits.${period as PeriodsEnum}`)}
              <span className={cn('block-title', { period: true })}>{limit.title}</span>
            </div>
            <span className={cn('value')}>
              {correctingTrailingNumbers(limit.init_value, displayDigits, totalDigits)}
            </span>
            <span className={cn('value', { type: 'fiat' })}>
              {correctingTrailingNumbers(limit.fiat_init_value, 2, 2)} {fiatCurrency}
            </span>
            <div className={cn('diagram-container')}>
              <div className={cn('diagram', { type: 'total' })} />
              <div className={cn('diagram', { type: 'used' })} style={{ width: `${percent}%` }} />
            </div>
            <div className={cn('legend')}>
              <span className={cn('value', { type: 'used' })}>
                {correctingTrailingNumbers(
                  +limit.init_value - +limit.value,
                  displayDigits,
                  totalDigits
                )}{' '}
                {currency} {t('limits.used')}
              </span>
              <span className={cn('value', { type: 'left' })}>
                {correctingTrailingNumbers(limit.value, displayDigits, totalDigits)} {currency}{' '}
                {t('limits.remained')}
              </span>
            </div>
            <div className={cn('legend', { type: 'fiat' })}>
              <span className={cn('value', { type: 'used' })}>
                {correctingTrailingNumbers(+limit.fiat_init_value - +limit.fiat_value, 2, 2)}{' '}
                {fiatCurrency}
              </span>
              <span className={cn('value', { type: 'left' })}>
                {correctingTrailingNumbers(+limit.fiat_value, 2, 2)} {fiatCurrency}
              </span>
            </div>
          </div>
        );
      })}
      <div className={cn('block')}>
        {min && min !== '0' && (
          <div>
            {t('operations.min')}: {min} {currency}
          </div>
        )}
        {max && max !== '0' && (
          <div>
            {t('operations.max')}: {max} {currency}
          </div>
        )}
      </div>
    </div>
  );
};

export default LimitBlock;
